<template>
  <section class="support-ticket-list">
    <ticket-filters @change="setFilters">
      <template v-if="canEdit && !showDraft" #create><app-button design="new" @click.native="openDraft">Add ticket</app-button></template>
      <template #actions><ticket-column-picker :value="visibleColumnKeys" :columns="columns" @input="updateVisibleColumns" /></template>
    </ticket-filters>

    <app-card v-if="showDraft" design="new" padding="compact" class="support-ticket-list__quick-add">
      <div v-if="showDraft" class="support-ticket-list__draft" @keydown.esc="closeDraft">
        <app-input ref="draftSubject" v-model="draft.subject" design="new" placeholder="Write a ticket title" />
        <app-select v-model="draft.requester" design="new" :options="requesters" label="full_name" placeholder="Requester" @search="searchRequesters" />
        <app-select v-model="draft.system" design="new" :options="systems" label="name" placeholder="System" @input="draftSystemChanged" />
        <app-input v-model="draft.description" design="new" placeholder="Short description" @keydown.enter.native="createTicket" />
        <button type="button" class="support-ticket-list__more-fields" @click="showDraftDetails = !showDraftDetails">{{ showDraftDetails ? 'Less' : 'More fields' }}</button>
        <app-button design="new" theme="success" size="small" :disabled="!canCreate || creating" @click.native="createTicket">{{ creating ? 'Creating…' : 'Create' }}</app-button>
        <button type="button" class="support-ticket-list__cancel" @click="closeDraft">Cancel</button>
      </div>
      <div v-if="showDraft && showDraftDetails" class="support-ticket-list__draft-details">
        <app-select v-model="draft.assignee" design="new" :options="assignees" label="full_name" placeholder="Assignee" />
        <app-select v-model="draft.direction" design="new" :options="draftDirections" label="name" placeholder="Direction" :disabled="!draft.system" @input="draftDirectionChanged" />
        <app-select v-model="draft.module" design="new" :options="draftModules" label="name" placeholder="Module" :disabled="!draft.direction" />
        <app-select v-model="draft.tags" design="new" :options="tags" multiple label="name" placeholder="Tags" />
        <app-select v-model="draft.labels" design="new" :options="labels" multiple label="name" placeholder="Responsibility" />
      </div>
      <p v-if="draftError" class="support-ticket-list__draft-error">{{ draftError }}</p>
    </app-card>

    <AppErrorState v-if="error" :message="error" @retry="retryLoad" />

    <section v-for="group in visibleGroups" :key="group.status" class="support-ticket-list__group">
      <app-card design="new" variant="table" header-density="compact" :title="group.label" :count="group.count" class="support-ticket-list__table-card">
        <div v-if="loading && !group.items.length" role="status" aria-label="Loading support tickets" aria-busy="true">
          <app-data-table design="new" density="dense" :columns="visibleColumns" :rows="[]" :min-width="tableMinWidth" :skeleton-rows="6" loading />
        </div>
        <app-no-results v-else-if="!error && !group.items.length" design="new" small :title="`No ${group.label.toLowerCase()} tickets`" text="" />
        <support-ticket-table v-else-if="group.items.length" :tickets="group.items" :columns="visibleColumns" :min-width="tableMinWidth" :loading="loading">
          <template #cell-ticket="{ ticket }">
            <div class="support-ticket-list__ticket-cell">
              <AppIconButton :label="ticket.is_important ? 'Remove important mark' : 'Mark as important'" :disabled="!canEdit || isWorking(ticket)" @click="saveMetadata(ticket, 'is_important', !ticket.is_important)"><i :class="ticket.is_important ? 'pi pi-star-fill' : 'pi pi-star'" aria-hidden="true" /></AppIconButton>
              <button type="button" class="support-ticket-list__ticket" @click="openTicket(ticket)">
                <strong>{{ ticket.public_number }}</strong>
              </button>
            </div>
          </template>
          <template #cell-description="{ ticket }">
            <AppTableText :text="[ticket.subject, ticket.description_preview].filter(Boolean).join(' — ')" />
          </template>
          <template #cell-system="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'system')" :value="ticket.system" :options="systems" can-create kind="systems" @created="dictionaryCreated" @input="saveMetadata(ticket, 'system', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :disabled="!canEdit" @click="startEditing(ticket, 'system')"><app-status-badge v-if="ticket.system" design="new" compact :label="ticket.system.name" :color="ticket.system.color" /><span v-else>Set system</span></button>
          </template>
          <template #cell-direction="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'direction')" :value="ticket.direction" :options="directionsFor(ticket)" can-create kind="directions" :parent-payload="{ system: ticket.system.id }" @created="dictionaryCreated" @input="saveMetadata(ticket, 'direction', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :disabled="!canEdit" @click="startEditing(ticket, 'direction')"><AppTableText :text="ticket.direction ? ticket.direction.name : 'Add direction'" /></button>
          </template>
          <template #cell-module="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'module')" :value="ticket.module" :options="modulesFor(ticket)" can-create kind="modules" :parent-payload="{ direction: ticket.direction.id }" @created="dictionaryCreated" @input="saveMetadata(ticket, 'module', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :disabled="!canEdit" @click="startEditing(ticket, 'module')"><AppTableText :text="ticket.module ? ticket.module.name : 'Add module'" /></button>
          </template>
          <template #cell-tags="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'tags')" :value="ticket.tags" :options="tags" multiple can-create kind="tags" @created="dictionaryCreated" @input="saveMetadata(ticket, 'tags', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button support-ticket-list__cell-button--badges" :disabled="!canEdit" @click="startEditing(ticket, 'tags')"><ticket-table-badges :items="ticket.tags" /></button>
          </template>
          <template #cell-responsibility="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'responsible_labels')" :value="ticket.responsible_labels" :options="labels" multiple can-create kind="responsible_labels" @created="dictionaryCreated" @input="saveMetadata(ticket, 'responsible_labels', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button support-ticket-list__cell-button--badges" :disabled="!canEdit" @click="startEditing(ticket, 'responsible_labels')"><ticket-table-badges :items="ticket.responsible_labels" /></button>
          </template>
          <template #cell-requester="{ ticket }"><ticket-table-person :user="ticket.requester" empty-text="Unknown requester" /></template>
          <template #cell-status="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'status')" :value="statusOption(ticket.status)" :options="activeStatusOptions" label="label" @input="saveStatus(ticket, $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :disabled="!canEdit" @click="startEditing(ticket, 'status')"><app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" /></button>
          </template>
          <template #cell-assignee="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'assignee')" :value="ticket.assignee" :options="assignees" label="full_name" placeholder="Unassigned" @input="saveMetadata(ticket, 'assignee', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :disabled="!canEdit" @click="startEditing(ticket, 'assignee')"><ticket-table-person :user="ticket.assignee" /></button>
          </template>
          <template #cell-priority="{ ticket }">
            <ticket-inline-select v-if="isEditing(ticket, 'priority')" :value="priorityOption(ticket.priority)" :options="priorityOptions" label="label" @input="saveMetadata(ticket, 'priority', $event)" @cancel="stopEditing" />
            <button v-else type="button" class="support-ticket-list__cell-button" :aria-label="`${priorityLabel(ticket.priority)} priority`" :disabled="!canEdit" @click="startEditing(ticket, 'priority')">
              <ticket-priority-indicator v-if="ticket.priority !== 'medium'" :priority="ticket.priority" show-label />
              <span v-else class="support-ticket-list__muted">—</span>
            </button>
          </template>
          <template #cell-created="{ ticket }"><span v-tooltip="tooltip(formatDateTime(ticket.created_at || ticket.status_changed_at))">{{ shortDate(ticket.created_at || ticket.status_changed_at) }}</span></template>
        </support-ticket-table>
        <footer v-if="group.hasMore" class="support-ticket-list__more">
          <app-button design="new" size="small" theme="dark" :disabled="group.loadingMore" @click.native="loadMore(group)">{{ group.loadingMore ? 'Loading…' : 'Load more' }}</app-button>
        </footer>
      </app-card>
    </section>

    <modal name="support-list-waiting-reason" :width="460" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="support-waiting-dialog">
        <app-modal-header design="new" title="What are we waiting for?" @close="cancelWaiting" />
        <div class="support-waiting-dialog__body">
          <label v-for="option in waitingOptions" :key="option.value">
            <input v-model="waitingFor" type="radio" :value="option.value">
            <span><strong>{{ option.label }}</strong><small>{{ option.help }}</small></span>
          </label>
          <footer><app-button design="new" theme="dark" @click.native="cancelWaiting">Cancel</app-button><app-button design="new" theme="success" :disabled="!waitingFor" @click.native="confirmWaiting">Move to Waiting</app-button></footer>
        </div>
      </section>
    </modal>
  </section>
</template>

<script>
import {
  getSupportTicketDictionary,
  getSupportTickets,
  getSupportTicketUsers,
  patchSupportTicket,
  postSupportTicket,
  postSupportTicketTransition
} from '@/http'
import { techSupportKey } from '@/utils/techSupportAccess'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import SupportTicketTable from './components/SupportTicketTable'
import TicketColumnPicker from './components/TicketColumnPicker'
import TicketFilters from './components/TicketFilters'
import TicketInlineSelect from './components/TicketInlineSelect'
import TicketPriorityIndicator from './components/TicketPriorityIndicator'
import TicketTableBadges from './components/TicketTableBadges'
import TicketTablePerson from './components/TicketTablePerson'
import {
  apiError,
  formatDateTime,
  idempotencyKey,
  normalizePage,
  PRIORITY_OPTIONS,
  priorityLabel,
  priorityOption,
  STATUS_OPTIONS,
  statusLabel,
  statusOption,
  statusTone
} from './ticketState'

const emptyDraft = () => ({
  subject: '',
  description: '',
  requester: null,
  assignee: null,
  system: null,
  direction: null,
  module: null,
  tags: [],
  labels: []
})

const SUPPORT_TICKET_COLUMN_STORAGE_KEY = 'support-ticket-list-visible-columns:v1'
const SUPPORT_TICKET_COLUMNS = [
  { key: 'ticket', label: 'Ticket', width: '130px' },
  { key: 'priority', label: 'Priority', width: '95px' },
  { key: 'description', label: 'Description', width: '170px' },
  { key: 'system', label: 'System', width: '115px' },
  { key: 'direction', label: 'Direction', width: '125px' },
  { key: 'module', label: 'Module', width: '115px' },
  { key: 'tags', label: 'Tags', width: '115px' },
  { key: 'responsibility', label: 'Responsibility', width: '130px' },
  { key: 'requester', label: 'Requester', width: '140px' },
  { key: 'status', label: 'Status', width: '115px' },
  { key: 'assignee', label: 'Assignee', width: '140px' },
  { key: 'created', label: 'Created', width: '90px' }
]

export default {
  name: 'SupportTicketList',
  components: { AppErrorState, AppIconButton, AppTableText, SupportTicketTable, TicketColumnPicker, TicketFilters, TicketInlineSelect, TicketPriorityIndicator, TicketTableBadges, TicketTablePerson },
  data() {
    return {
      columns: SUPPORT_TICKET_COLUMNS,
      visibleColumnKeys: SUPPORT_TICKET_COLUMNS.map(column => column.key),
      groups: [
        { status: 'new', label: 'New', items: [], count: 0, page: 1, hasMore: false, loadingMore: false },
        { status: 'in_progress', label: 'In progress', items: [], count: 0, page: 1, hasMore: false, loadingMore: false },
        { status: 'waiting', label: 'Waiting', items: [], count: 0, page: 1, hasMore: false, loadingMore: false }
      ],
      filters: {},
      loading: true,
      error: '',
      editing: null,
      working: {},
      systems: [],
      directions: [],
      modules: [],
      tags: [],
      labels: [],
      assignees: [],
      requesters: [],
      priorityOptions: PRIORITY_OPTIONS,
      activeStatusOptions: STATUS_OPTIONS.filter(option => option.value !== 'closed'),
      showDraft: false,
      showDraftDetails: false,
      draft: emptyDraft(),
      creating: false,
      draftError: '',
      waitingTicket: null,
      waitingFor: 'requester',
      waitingOptions: [
        { value: 'requester', label: 'Requester', help: 'The employee needs to answer or add information.' },
        { value: 'internal_team', label: 'Internal team', help: 'Another colleague or developer needs to respond.' },
        { value: 'external_party', label: 'External party', help: 'A vendor or external system is blocking progress.' }
      ]
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('support', 'edit')) },
    initialStatus() { return this.$route.query.status || (this.$route.query.focus === 'waiting' ? 'waiting' : '') },
    visibleGroups() { return this.initialStatus ? this.groups.filter(group => group.status === this.initialStatus) : this.groups },
    draftDirections() { return this.directions.filter(item => item.system === this.draft.system?.id) },
    draftModules() { return this.modules.filter(item => item.direction === this.draft.direction?.id) },
    canCreate() { return Boolean(this.draft.subject.trim() && this.draft.description.trim() && this.draft.requester && this.draft.system) },
    visibleColumns() { return this.columns.filter(column => this.visibleColumnKeys.includes(column.key)) },
    tableMinWidth() { return '1200px' }
  },
  created() {
    this.restoreVisibleColumns()
    this.loadOptions()
    this.load()
    this.$root.$on('support-ticket-refresh', this.load)
  },
  beforeDestroy() {
    this.$root.$off('support-ticket-refresh', this.load)
  },
  methods: {
    formatDateTime,
    priorityLabel,
    priorityOption,
    statusLabel,
    statusOption,
    statusTone,
    columnStorageKey() {
      const userId = this.$store.state.userData?.id || 'anonymous'
      return `${SUPPORT_TICKET_COLUMN_STORAGE_KEY}:${userId}`
    },
    restoreVisibleColumns() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.columnStorageKey()))
        if (!Array.isArray(stored)) return
        const available = this.columns.map(column => column.key)
        const restored = available.filter(key => key === 'ticket' || stored.includes(key))
        if (restored.length) this.visibleColumnKeys = restored
      } catch (error) {
        // Ignore malformed local preferences and use the complete default column set.
      }
    },
    updateVisibleColumns(keys) {
      const available = this.columns.map(column => column.key)
      this.visibleColumnKeys = available.filter(key => key === 'ticket' || keys.includes(key))
      this.stopEditing()
      try {
        window.localStorage.setItem(this.columnStorageKey(), JSON.stringify(this.visibleColumnKeys))
      } catch (error) {
        // Storage can be unavailable in private browsing; the current session still keeps the selection.
      }
    },
    loadOptions() {
      return Promise.all([
        getSupportTicketDictionary('systems', { active: true, page_size: 100 }),
        getSupportTicketDictionary('directions', { active: true, page_size: 100 }),
        getSupportTicketDictionary('modules', { active: true, page_size: 100 }),
        getSupportTicketDictionary('tags', { active: true, page_size: 100 }),
        getSupportTicketDictionary('responsible_labels', { active: true, page_size: 100 }),
        getSupportTicketUsers({ kind: 'assignee', page_size: 100 }),
        getSupportTicketUsers({ page_size: 100 })
      ]).then(([systems, directions, modules, tags, labels, assignees, requesters]) => {
        this.systems = normalizePage(systems.data)
        this.directions = normalizePage(directions.data)
        this.modules = normalizePage(modules.data)
        this.tags = normalizePage(tags.data)
        this.labels = normalizePage(labels.data)
        this.assignees = normalizePage(assignees.data)
        this.requesters = normalizePage(requesters.data)
      }).catch(error => { this.error = apiError(error, 'Unable to load ticket options.') })
    },
    retryLoad() {
      this.error = ''
      this.loadOptions()
      this.load()
    },
    load(showLoading = true) {
      if (showLoading) this.loading = true
      this.error = ''
      const groups = this.visibleGroups
      return Promise.all(groups.map(group => getSupportTickets({ ...this.filters, status: group.status, page: 1, page_size: 40 }))).then(responses => {
        responses.forEach((response, index) => {
          const group = groups[index]
          group.items = normalizePage(response.data)
          group.count = response.data.count ?? group.items.length
          group.page = 1
          group.hasMore = Boolean(response.data.next)
        })
      }).catch(error => { this.error = apiError(error, 'Unable to load support tickets.') })
        .finally(() => { this.loading = false })
    },
    loadMore(group) {
      if (!group.hasMore || group.loadingMore) return
      group.loadingMore = true
      getSupportTickets({ ...this.filters, status: group.status, page: group.page + 1, page_size: 40 }).then(response => {
        const known = new Set(group.items.map(ticket => ticket.id))
        group.items = [...group.items, ...normalizePage(response.data).filter(ticket => !known.has(ticket.id))]
        group.page += 1
        group.hasMore = Boolean(response.data.next)
        group.count = response.data.count ?? group.items.length
      }).catch(error => { this.error = apiError(error) }).finally(() => { group.loadingMore = false })
    },
    setFilters(filters) { this.filters = filters; this.load() },
    openTicket(ticket) { this.$router.push({ name: this.$route.name, query: { ...this.$route.query, ticket: ticket.id } }).catch(() => {}) },
    tooltip(content) { return { content: content || '—', placement: 'top', offset: 8, classes: 'tooltip--new' } },
    shortDate(value) { return value ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(new Date(value)) : '—' },
    startEditing(ticket, field) {
      if (!this.canEdit) return
      if (field === 'direction' && !ticket.system) return
      if (field === 'module' && !ticket.direction) return
      this.editing = { ticketId: ticket.id, field }
    },
    stopEditing() { this.editing = null },
    isEditing(ticket, field) { return this.editing?.ticketId === ticket.id && this.editing.field === field },
    isWorking(ticket) { return Boolean(this.working[ticket.id]) },
    directionsFor(ticket) { return this.directions.filter(item => item.system === ticket.system?.id) },
    modulesFor(ticket) { return this.modules.filter(item => item.direction === ticket.direction?.id) },
    dictionaryCreated({ kind, item }) {
      const target = kind === 'responsible_labels' ? 'labels' : kind
      if (Array.isArray(this[target]) && !this[target].some(value => value.id === item.id)) this[target].push(item)
    },
    metadataPayload(ticket, field, value) {
      if (field === 'system') return { system: value.id, direction: null, module: null }
      if (field === 'direction') return { direction: value?.id ?? null, module: null }
      if (['system', 'direction', 'module', 'assignee'].includes(field)) return { [field]: value?.id ?? null }
      if (['tags', 'responsible_labels'].includes(field)) return { [field]: (value || []).map(item => item.id) }
      if (field === 'priority') return { priority: value.value }
      return { [field]: value }
    },
    async saveMetadata(ticket, field, value) {
      if (!this.canEdit) return
      if (this.isWorking(ticket)) return
      if ((field === 'system' || field === 'priority') && !value) return
      this.stopEditing()
      this.$set(this.working, ticket.id, true)
      try {
        const response = await patchSupportTicket(ticket.id, { ...this.metadataPayload(ticket, field, value), expected_state_version: ticket.state_version })
        this.replaceTicket(response.data)
      } catch (error) {
        this.error = apiError(error)
        await this.load(false)
      } finally {
        this.$delete(this.working, ticket.id)
      }
    },
    saveStatus(ticket, option) {
      if (!this.canEdit) return
      this.stopEditing()
      if (!option || option.value === ticket.status) return
      if (option.value === 'waiting') {
        this.waitingTicket = ticket
        this.waitingFor = 'requester'
        this.$modal.show('support-list-waiting-reason')
        return
      }
      this.transition(ticket, option.value)
    },
    async transition(ticket, status, waitingFor = null) {
      if (this.isWorking(ticket)) return
      this.$set(this.working, ticket.id, true)
      try {
        const response = await postSupportTicketTransition(ticket.id, { status, waiting_for: waitingFor, expected_state_version: ticket.state_version }, idempotencyKey())
        this.replaceTicket(response.data)
      } catch (error) {
        this.error = apiError(error)
        await this.load(false)
      } finally {
        this.$delete(this.working, ticket.id)
      }
    },
    replaceTicket(ticket) {
      this.groups.forEach(group => {
        const index = group.items.findIndex(item => item.id === ticket.id)
        if (index !== -1) { group.items.splice(index, 1); group.count = Math.max(0, group.count - 1) }
      })
      const target = this.groups.find(group => group.status === ticket.status)
      if (target) {
        target.items.unshift(ticket)
        if (target.status === 'new') target.items.sort((left, right) => new Date(left.status_changed_at) - new Date(right.status_changed_at))
        target.count += 1
      }
    },
    confirmWaiting() {
      const ticket = this.waitingTicket
      this.cancelWaiting()
      if (ticket) this.transition(ticket, 'waiting', this.waitingFor)
    },
    cancelWaiting() { this.$modal.hide('support-list-waiting-reason'); this.waitingTicket = null },
    openDraft() {
      if (!this.canEdit) return
      this.showDraft = true
      this.showDraftDetails = false
      this.draft = emptyDraft()
      this.draft.system = this.systems.find(item => item.name === 'Board') || this.systems[0] || null
      this.$nextTick(() => this.$refs.draftSubject?.$el?.focus())
    },
    closeDraft() { this.showDraft = false; this.showDraftDetails = false; this.draft = emptyDraft(); this.draftError = '' },
    draftSystemChanged(value) {
      if (this.draft.direction?.system !== value?.id) {
        this.draft.direction = null
        this.draft.module = null
      }
    },
    draftDirectionChanged(value) {
      if (this.draft.module?.direction !== value?.id) this.draft.module = null
    },
    searchRequesters(search, loading) {
      if (!search || search.length < 2) return
      loading(true)
      getSupportTicketUsers({ search, page_size: 30 }).then(response => { this.requesters = normalizePage(response.data) }).finally(() => loading(false))
    },
    async createTicket() {
      if (!this.canCreate || this.creating) return
      this.creating = true
      this.draftError = ''
      try {
        const response = await postSupportTicket({
          requester: this.draft.requester.id,
          subject: this.draft.subject.trim(),
          description: this.draft.description.trim(),
          system: this.draft.system.id,
          direction: this.draft.direction?.id || null,
          module: this.draft.module?.id || null,
          assignee: this.draft.assignee?.id || null,
          tags: this.draft.tags.map(item => item.id),
          responsible_labels: this.draft.labels.map(item => item.id),
          page_context: { route_name: this.$route.name, path: this.$route.path, page_title: this.$route.meta?.title || document.title }
        }, idempotencyKey())
        this.closeDraft()
        this.$notify({ type: 'success', text: `${response.data.public_number} created.` })
        this.$root.$emit('support-ticket-created', response.data)
        await this.load(false)
      } catch (error) {
        this.draftError = apiError(error)
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.support-ticket-list { display: grid; gap: $ui-space-16; }
.support-ticket-list__quick-add { margin-bottom: 0; }
.support-ticket-list__toolbar { display: flex; min-height: 36px; align-items: center; gap: $ui-space-12; }
.support-ticket-list__toolbar > span { color: $ui-ink; font: 600 $ui-font-size-body $ui-font-heading; }
.support-ticket-list__draft { display: grid; grid-template-columns: minmax(190px, 1.2fr) minmax(190px, 1fr) 170px minmax(210px, 1fr) auto auto auto; align-items: center; gap: $ui-space-8; margin-top: $ui-space-12; }
.support-ticket-list__draft-details { display: grid; grid-template-columns: repeat(5, minmax(170px, 230px)); gap: $ui-space-8; margin-top: $ui-space-12; }
.support-ticket-list__more-fields { border: 0; background: transparent; color: $ui-blue; cursor: pointer; font: 600 $ui-font-size-metadata $ui-font-body; white-space: nowrap; }
.support-ticket-list__cancel { border: 0; background: transparent; color: $ui-muted; cursor: pointer; font: 600 $ui-font-size-metadata $ui-font-body; }
.support-ticket-list__draft-error,
.support-ticket-list__error { color: $ui-red; }
.support-ticket-list__draft-error { margin: $ui-space-8 0 0; }
.support-ticket-list__error { display: flex; justify-content: space-between; padding: $ui-space-12; border-radius: $ui-control-radius; background: rgba($ui-red, .08); }
.support-ticket-list__error button { border: 0; background: transparent; color: inherit; cursor: pointer; font-weight: 700; }
.support-ticket-list__group { display: grid; gap: $ui-space-8; }
.support-ticket-list__table-card { overflow: hidden; }
.support-ticket-list__ticket-cell { display: flex; min-width: 0; align-items: center; gap: $ui-space-4; }
.support-ticket-list__ticket { display: flex; width: 100%; min-width: 0; align-items: center; gap: $ui-space-8; padding: 0; border: 0; background: transparent; cursor: pointer; font: inherit; text-align: left; }
.support-ticket-list__ticket strong { flex: 0 0 auto; color: $ui-blue; }
.support-ticket-list__ellipsis { display: block; overflow: hidden; color: $ui-ink; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-list__ellipsis { color: $ui-text; font-weight: 400; }
.support-ticket-list__description { display: block; overflow: hidden; color: $ui-text; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-list__description strong { color: $ui-ink; font-weight: 600; }
.support-ticket-list__cell-button { display: flex; width: 100%; min-width: 0; min-height: 34px; align-items: center; overflow: hidden; padding: 2px 5px; border: 1px solid transparent; border-radius: 7px; background: transparent; color: $ui-text; cursor: pointer; font: 500 $ui-font-size-metadata $ui-font-body; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.support-ticket-list__cell-button:hover,
.support-ticket-list__cell-button:focus-visible { border-color: $ui-line-strong; background: $ui-surface-soft; outline: 0; }
.support-ticket-list__cell-button--badges { padding: 2px; }
.support-ticket-list__muted { color: $ui-muted; }
.support-ticket-list__more { display: flex; justify-content: center; padding: $ui-space-12; border-top: 1px solid $ui-line; }
.support-waiting-dialog { background: $ui-page; }
.support-waiting-dialog__body { display: grid; gap: $ui-space-12; padding: $ui-space-20; }
.support-waiting-dialog label { display: flex; gap: $ui-space-12; padding: $ui-space-12; border: 1px solid $ui-line; border-radius: $ui-panel-radius; background: $ui-surface; cursor: pointer; }
.support-waiting-dialog label span { display: grid; gap: $ui-space-4; }
.support-waiting-dialog small { color: $ui-muted; font-size: $ui-font-size-metadata; }
.support-waiting-dialog footer { display: flex; justify-content: flex-end; gap: $ui-space-12; margin-top: $ui-space-8; }
@media (max-width: 1250px) { .support-ticket-list__draft { grid-template-columns: repeat(2, minmax(0, 1fr)); } .support-ticket-list__draft-details { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 650px) { .support-ticket-list__draft, .support-ticket-list__draft-details { grid-template-columns: minmax(0, 1fr); } }
</style>
